import type { PlanSummary } from '@developerz/domain';
import { IconLayers } from '@developerz/ui';
import { A } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { For, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { absolute, ago } from '../lib/time';
import { StatusPill } from './StatusPill';
import { buttonClass } from './ui/Button';

// One plan as a `.row-list` row (the DigestCard shape): headline over a meta
// line, deep link right. Presenter — the Plans route owns the read.
//
// The HEADLINE is `leadTitle`, the lowest-position slice's title, because a plan
// has no title column of its own: the id is caller-minted (`run_<32 hex>` for a
// scout-authored plan) and unreadable. The id still renders, in the mono voice,
// since it is what `plan get` and `GET /v1/plans/:planId` are addressed by.
//
// PROGRESS IS MERGED-OVER-TOTAL, not a percentage: a two-slice plan at 50% and a
// ten-slice plan at 50% are not the same amount of work left, and the fraction
// says which one you are looking at. It reads `success` only once the PLAN
// ROW itself is `done` (#5137) — not off a merged/total count match, which
// would wear the done tone up to one `cron.dead_dependency` tick before the
// system's own writer agrees, disagreeing with `planBucket`'s tab on the same
// screen. A partial can never wear the done colour either way.
//
// THE REMAINDER IS NOT ALL "IN PROGRESS", which is what the fraction alone said
// (#2525 follow-up 3). `stalledCount` is the slices that can never reach `merged`
// — task failed, no PR linked, PR closed unmerged — and on prod 2026-08-27 that
// was 529 of 583 groups. A destructive chip beside the fraction, rendered only
// when it is non-zero: an epic with nothing dead should look exactly as it did.
//
// ONE CHIP PER REPO, LEAD FIRST (arc 101 slice 05). A plan is one ask and its
// slices may live in several repositories, so a single name here is a claim
// about the plan that is false for every slice but one. The order is the LEAD's
// repo (`repoFullName` — the lowest-position slice, the same slice `leadTitle`
// names) and then the rest: `repoFullNames` arrives alphabetical, so painting it
// verbatim would put an unrelated slice's repo where the headline's own belongs.
//
// The lead is read off `repoFullName` rather than assumed to be
// `repoFullNames[0]`, and `repoFullNames` itself is guarded — both for the same
// ROLL WINDOW, which is one deploy wide and is ahead of this code rather than
// behind it, since the field is new in the PR that added these lines.
//
// The window is real, not defensive. `usePlans` reads with the CAST overload of
// `apiFetch` and passes no schema (`lib/plan.ts`), and `parseBody` returns the
// body untouched when no schema is given (`lib/api-errors.ts` — "a drifted
// field renders `undefined`", deliberately, because a blanket parse would blank
// a working page). `GET /v1/plans` hands the port's rows back verbatim
// (`apps/api/src/routes/plans.ts`). So a browser holding this bundle while an
// API pod that predates `repoFullNames` answers gets `undefined` here, with
// nothing in between to refuse it — and a bare `<For each={repoFullNames}>`
// would paint NO repo at all until that pod rolled.
//
// THE CONTRACT IN THAT WINDOW: the lead repo alone, which is exactly what this
// card showed before the field existed. Pinned by "falls back to the lead repo
// alone when the API has not rolled yet" in `Plans.test.tsx`, so it is a branch
// that runs rather than one nobody has seen.
//
// RETIRE IT once every pod serving `/v1/plans` is past this PR — the first
// completed roll after it merges. It cannot outlive its purpose by more than
// that: `repoFullName` is itself scheduled for removal once no reader wants a
// single name (`PlanSummarySchema`), and when it goes this whole expression
// stops compiling rather than sitting here as a dead ternary.

export type PlanCardProps = {
  readonly plan: PlanSummary;
};

export function PlanCard(props: PlanCardProps): JSX.Element {
  // The plan's OWN status, not a count match (#5137): `planBucket`
  // (`@developerz/domain`) reads `status === 'done'`, written by the system
  // once every group is SATISFIED (merged, a completed `report`/`ops_action`
  // slice, or a `pr` slice whose run settled no work) — a fully-merged epic
  // can sit `open` for up to one `cron.dead_dependency` tick before that
  // write lands, and a count-based check here would wear the done tone a
  // tick early, disagreeing with the fleet page's own tab.
  const done = (): boolean => props.plan.status === 'done';
  const repos = (): readonly string[] => [
    props.plan.repoFullName,
    ...(props.plan.repoFullNames ?? []).filter((repo) => repo !== props.plan.repoFullName),
  ];

  return (
    <li class="row-list__row plan-row">
      <div class="row-list__lead">
        <span class="row-list__tile">
          <IconLayers size="1rem" />
        </span>
        <span class="plan-row__text">
          <span class="plan-row__title">{props.plan.leadTitle}</span>
          <span class="plan-row__meta">
            <For each={repos()}>{(repo) => <span class="plan-row__repo">{repo}</span>}</For>
            <StatusPill tone={done() ? 'success' : 'neutral'}>
              {t('fleet.plans.progress', {
                merged: String(props.plan.mergedCount),
                total: String(props.plan.groupCount),
              })}
            </StatusPill>
            <Show when={props.plan.stalledCount > 0}>
              <StatusPill tone="danger">
                {t('fleet.plans.stalled', { count: String(props.plan.stalledCount) })}
              </StatusPill>
            </Show>
            {/* The absolute instant rides in `title`: "2 days ago" is the
                scannable form, but an operator correlating an epic with a run
                log needs the timestamp itself. */}
            <span title={absolute(props.plan.updatedIso)}>
              {t('fleet.plans.updated', { when: ago(props.plan.updatedIso) })}
            </span>
            <code class="plan-row__id">{props.plan.planId}</code>
          </span>
        </span>
      </div>
      {/* Named by the plan's headline, not "Open": a page of identical link
          labels is a screen reader reading "open, open, open". */}
      <A
        class={buttonClass({ variant: 'ghost', size: 'sm' })}
        href={orgHref(`/fleet/plans/${encodeURIComponent(props.plan.planId)}`)}
        aria-label={t('fleet.plans.open_aria', { title: props.plan.leadTitle })}
      >
        {t('fleet.plans.open')}
      </A>
    </li>
  );
}
